import type { CachedPlaylist, Playlist, ZipMode } from 'types/playlist'
import type { PublicPlaylist } from 'types/public-playlist'
import type { Shortlist } from 'types/shortlist'

import { isAiff } from '@disco/utils/file-utils'
import {
  getExtraFormat,
  getOptimizedFormat,
  getOriginalFormat,
} from '@disco/utils/tracks'

/**
 * Checks if a playlist is read-only.
 *
 * Determines whether the playlist can be modified based on its read_only flag.
 *
 * @param playlist - The playlist to check
 * @returns True if the playlist is read-only, false otherwise
 *
 * @example
 * ```typescript
 * const readOnlyPlaylist = { read_only: true, name: 'System Playlist' }
 * isReadOnlyPlaylist(readOnlyPlaylist) // returns true
 *
 * const editablePlaylist = { read_only: false, name: 'My Playlist' }
 * isReadOnlyPlaylist(editablePlaylist) // returns false
 *
 * isReadOnlyPlaylist(null) // returns false
 * ```
 */
export const isReadOnlyPlaylist = (playlist?: Playlist | null): boolean => {
  return playlist?.read_only || false
}

/**
 * Enhances playlist tracks with format column information.
 *
 * Processes tracks to add formatColumns array containing available formats
 * in optimal order (optimized, original, extra), with special handling for AIFF files.
 *
 * @param params - Object containing tracks array
 * @param params.tracks - Array of track objects to enhance
 * @returns Array of tracks with formatColumns property added
 *
 * @example
 * ```typescript
 * const tracks = [
 *   {
 *     id: 1,
 *     all_files: {
 *       'mp3': { url: 'track.mp3' },
 *       'wav': { url: 'track.wav' }
 *     }
 *   }
 * ]
 *
 * const enhanced = getPlaylistTracksWithFormats({ tracks })
 * // Returns tracks with formatColumns: ['mp3', 'wav', null]
 * ```
 */
export const getPlaylistTracksWithFormats = ({
  tracks,
}: {
  // TODO: refactor Object
  tracks: any[]
}): any[] => {
  const tracksWithFormats = tracks.map<any>(track => {
    const optFormat = getOptimizedFormat(track) || null
    const originalFormat = getOriginalFormat(track) || null
    const extraFormat = getExtraFormat(track) || null

    let formatColumns = [optFormat, originalFormat, extraFormat]

    if (!optFormat) {
      formatColumns = [originalFormat, extraFormat]
    }

    if (isAiff(originalFormat) && extraFormat === 'wav') {
      formatColumns = [optFormat, extraFormat, originalFormat]
    }

    return {
      ...track,
      formatColumns,
    }
  })

  return tracksWithFormats
}

/**
 * Gets the default download link for a playlist based on zip mode.
 *
 * Retrieves the appropriate zip download URL based on the playlist's
 * default zip mode, with fallback to optimized version if needed.
 *
 * @param playlist - Playlist or public playlist object
 * @param mode - Zip mode ('public', 'private', etc.)
 * @returns Download URL string or null if not available
 *
 * @example
 * ```typescript
 * const playlist = {
 *   zip_urls: {
 *     public: {
 *       optimized: 'playlist-optimized.zip',
 *       original_else_optimized: 'playlist-mixed.zip'
 *     }
 *   },
 *   default_zip_mode: 'original_else_optimized'
 * }
 *
 * const url = getPlaylistDefaultDownloadLink(playlist, 'public')
 * // Returns 'playlist-mixed.zip'
 * ```
 */
export const getPlaylistDefaultDownloadLink = (
  { zip_urls, default_zip_mode }: Playlist | PublicPlaylist,
  mode: ZipMode,
): string | null | undefined => {
  if (!zip_urls || !zip_urls[mode] || !default_zip_mode) return null
  // original_else_optimized might not exist even if option is chosen,
  // load optimized url as a fallback
  const fallback =
    zip_urls[mode]['original_else_optimized'] || zip_urls[mode]['optimized']
  return zip_urls[mode][default_zip_mode] || fallback
}

/**
 * Gets the album image for a playlist with fallback options.
 *
 * Attempts to find an album image from multiple sources: explicit album image,
 * tracks album image, or first track's artwork.
 *
 * @param playlist - Playlist, public playlist, or shortlist object
 * @returns Album image URL or null if no image is found
 *
 * @example
 * ```typescript
 * const playlistWithImage = {
 *   album_image_url: 'custom-album.jpg',
 *   tracks_artworks: [{ artwork: 'track1.jpg' }]
 * }
 * getAlbumImage(playlistWithImage) // returns 'custom-album.jpg'
 *
 * const playlistWithTrackArt = {
 *   tracks_artworks: [{ artwork: 'track1.jpg' }]
 * }
 * getAlbumImage(playlistWithTrackArt) // returns 'track1.jpg'
 *
 * getAlbumImage(null) // returns undefined
 * ```
 */
export const getAlbumImage = (
  playlist?: Playlist | PublicPlaylist | Shortlist | null,
): string | null | undefined => {
  let image = playlist?.album_image_url ?? playlist?.tracks_album_image

  if (!image && playlist?.tracks_artworks?.length) {
    const firstArtwork = playlist.tracks_artworks[0]
    if (firstArtwork) {
      image = firstArtwork.artwork
    }
  }

  return image
}
